/**
 * Парсинг QR кода товара
 * 
 * Форматы:
 * - id (1 часть) - только ID товара
 * - id;price (2 части) - ID и цена для штучных товаров
 * - id;weight (2 части) - ID и вес для товаров в килограммах (цена будет пересчитана)
 * - id;weight;price (3 части) - ID, вес и цена для товаров в килограммах
 * 
 * @param text - Текст QR кода
 * @returns Объект с распарсенными данными: productId, weight, price, partsCount
 */
export interface ParsedQrCode {
    productId: number | null;
    weight: number | null;
    price: number | null;
    partsCount: number;
}

export const parseQrCode = (text: string): ParsedQrCode => {
    if (!text) {
        return { productId: null, weight: null, price: null, partsCount: 0 };
    }
    
    // Нормализация: заменяем 'ж'/'Ж' на ';' и 'ю'/'Ю' на '.' (проблема с русской раскладкой)
    const normalizedText = text.replace(/[жЖ]/g, ';').replace(/[юЮ]/g, '.');
    
    // Если нет разделителя, пытаемся распарсить как просто ID
    if (!normalizedText.includes(';')) {
        const productId = parseInt(normalizedText.trim(), 10);
        if (!isNaN(productId) && productId > 0) {
            return { productId, weight: null, price: null, partsCount: 1 };
        }
        return { productId: null, weight: null, price: null, partsCount: 0 };
    }
    
    const parts = normalizedText.split(';').map(p => p.trim());
    if (parts.length >= 1) {
        const productId = parseInt(parts[0], 10);
        
        if (isNaN(productId) || productId <= 0) {
            return { productId: null, weight: null, price: null, partsCount: parts.length };
        }
        
        let weight: number | null = null;
        let price: number | null = null;
        
        // Если 2 части: может быть id;price (штучный товар) или id;weight (товар в килограммах)
        // Парсер по умолчанию интерпретирует как id;price, но правильная интерпретация будет сделана позже на основе типа товара
        if (parts.length === 2 && parts[1]) {
            const secondPartValue = parseFloat(parts[1]);
            if (!isNaN(secondPartValue) && secondPartValue >= 0) {
                // По умолчанию интерпретируем как цену, но это может быть исправлено позже
                price = secondPartValue;
            }
        }
        // Если 3 части: id;weight;price (товар в килограммах)
        else if (parts.length >= 3) {
            // Второй параметр - вес
            if (parts[1]) {
                const weightValue = parseFloat(parts[1]);
                if (!isNaN(weightValue) && weightValue > 0) {
                    weight = weightValue;
                }
            }
            // Третий параметр - цена
            if (parts[2]) {
                const priceValue = parseFloat(parts[2]);
                if (!isNaN(priceValue) && priceValue >= 0) {
                    price = priceValue;
                }
            }
        }
        
        return { productId, weight, price, partsCount: parts.length };
    }
    
    return { productId: null, weight: null, price: null, partsCount: 0 };
};

